import React, { useState, useEffect } from 'react';
import { FiSave, FiUpload, FiGlobe, FiPhone, FiMail, FiMapPin, FiDollarSign, FiTruck, FiBell, FiShare2 } from 'react-icons/fi';
import API from '../../api/axios';
import { getImageUrl } from '../../utils/helpers';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';

const Section = ({ title, icon: Icon, children }) => (
  <div className="card p-6 mb-5">
    <div className="flex items-center gap-2 mb-5 pb-4 border-b border-gray-100">
      <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center">
        <Icon size={16} className="text-primary-600" />
      </div>
      <h2 className="font-bold text-gray-800">{title}</h2>
    </div>
    {children}
  </div>
);

const AdminSettings = () => {
  const { refreshSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    siteName: 'Hospitex',
    tagline: "Pakistan's Leading Medical Equipment Supplier",
    logo: '',
    phone: '+92 335 555 4828',
    phone2: '',
    email: 'hospitex@gmail.com',
    whatsapp: '+923355554828',
    address: 'Lahore, Punjab, Pakistan',
    city: 'Lahore',
    country: 'Pakistan',
    currency: 'PKR',
    currencySymbol: '₨',
    announcement: '',
    showAnnouncement: false,
    socialLinks: { facebook: '', instagram: '', youtube: '', twitter: '' },
    seo: { metaTitle: '', metaDescription: '', keywords: '' },
    shipping: { freeShippingThreshold: 5000, defaultShippingCost: 300 },
  });

  useEffect(() => {
    API.get('/settings').then(({ data }) => {
      setForm({
        siteName: data.siteName || '',
        tagline: data.tagline || '',
        logo: data.logo || '',
        phone: data.phone || '',
        phone2: data.phone2 || '',
        email: data.email || '',
        whatsapp: data.whatsapp || '',
        address: data.address || '',
        city: data.city || '',
        country: data.country || 'Pakistan',
        currency: data.currency || 'PKR',
        currencySymbol: data.currencySymbol || '₨',
        announcement: data.announcement || '',
        showAnnouncement: data.showAnnouncement || false,
        socialLinks: { facebook: data.socialLinks?.facebook || '', instagram: data.socialLinks?.instagram || '', youtube: data.socialLinks?.youtube || '', twitter: data.socialLinks?.twitter || '' },
        seo: { metaTitle: data.seo?.metaTitle || '', metaDescription: data.seo?.metaDescription || '', keywords: data.seo?.keywords || '' },
        shipping: { freeShippingThreshold: data.shipping?.freeShippingThreshold ?? 5000, defaultShippingCost: data.shipping?.defaultShippingCost ?? 300 },
      });
    }).catch(() => {});
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (parent, key, value) => setForm((f) => ({ ...f, [parent]: { ...f[parent], [key]: value } }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('logo', data.url);
      toast.success('Logo uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put('/settings', form);
      refreshSettings();
      toast.success('Settings saved successfully!');
    } catch {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Site Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Manage your store's configuration, branding, and contact information</p>
      </div>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-5">

            {/* Brand */}
            <Section title="Brand & Identity" icon={FiGlobe}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Site Name</label>
                  <input value={form.siteName} onChange={(e) => set('siteName', e.target.value)} className="input-field" placeholder="Hospitex" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Tagline</label>
                  <input value={form.tagline} onChange={(e) => set('tagline', e.target.value)} className="input-field" placeholder="Your brand tagline" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Logo</label>
                  <div className="flex items-center gap-4">
                    {form.logo ? (
                      <img src={getImageUrl(form.logo)} alt="Logo" className="h-14 w-auto object-contain rounded-lg border border-gray-200 bg-white p-1" />
                    ) : (
                      <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center">
                        <span className="text-primary-600 font-bold text-2xl">H</span>
                      </div>
                    )}
                    <label className="cursor-pointer flex items-center gap-2 border-2 border-dashed border-gray-300 hover:border-primary-400 rounded-xl px-5 py-3 transition-colors">
                      <FiUpload size={16} className="text-gray-400" />
                      <span className="text-sm text-gray-500">{uploading ? 'Uploading...' : 'Upload Logo'}</span>
                      <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploading} className="hidden" />
                    </label>
                    {form.logo && (
                      <button type="button" onClick={() => set('logo', '')} className="text-sm text-red-400 hover:text-red-600">Remove</button>
                    )}
                  </div>
                </div>
              </div>
            </Section>

            {/* Contact */}
            <Section title="Contact Information" icon={FiPhone}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Primary Phone</label>
                  <input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="input-field" placeholder="+92 300 0000000" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Secondary Phone</label>
                  <input value={form.phone2} onChange={(e) => set('phone2', e.target.value)} className="input-field" placeholder="+92 300 0000000" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Email Address</label>
                  <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="input-field" placeholder="info@hospitex.com.pk" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">WhatsApp Number</label>
                  <input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className="input-field" placeholder="+923000000000" />
                </div>
              </div>
            </Section>

            {/* Address */}
            <Section title="Business Address" icon={FiMapPin}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Full Address</label>
                  <textarea value={form.address} onChange={(e) => set('address', e.target.value)} rows={2} className="input-field" placeholder="Street address, area, building" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">City</label>
                  <input value={form.city} onChange={(e) => set('city', e.target.value)} className="input-field" placeholder="Lahore" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Country</label>
                  <input value={form.country} onChange={(e) => set('country', e.target.value)} className="input-field" placeholder="Pakistan" />
                </div>
              </div>
            </Section>

            {/* Social Links */}
            <Section title="Social Media Links" icon={FiShare2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'facebook', label: 'Facebook Page URL' },
                  { key: 'instagram', label: 'Instagram Profile URL' },
                  { key: 'youtube', label: 'YouTube Channel URL' },
                  { key: 'twitter', label: 'Twitter/X Profile URL' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="text-sm font-medium text-gray-700 mb-1 block capitalize">{label}</label>
                    <input
                      value={form.socialLinks[key]}
                      onChange={(e) => setNested('socialLinks', key, e.target.value)}
                      className="input-field"
                      placeholder={`https://${key}.com/yourpage`}
                    />
                  </div>
                ))}
              </div>
            </Section>

            {/* SEO */}
            <Section title="SEO Settings" icon={FiGlobe}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Meta Title</label>
                  <input value={form.seo.metaTitle} onChange={(e) => setNested('seo', 'metaTitle', e.target.value)} className="input-field" placeholder="Hospitex - Medical Equipment Pakistan" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Meta Description</label>
                  <textarea value={form.seo.metaDescription} onChange={(e) => setNested('seo', 'metaDescription', e.target.value)} rows={3} className="input-field" placeholder="Buy medical equipment online in Pakistan..." />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Keywords (comma separated)</label>
                  <input value={form.seo.keywords} onChange={(e) => setNested('seo', 'keywords', e.target.value)} className="input-field" placeholder="medical equipment, hospital furniture, Pakistan" />
                </div>
              </div>
            </Section>
          </div>

          {/* Right sidebar */}
          <div className="space-y-5">
            {/* Announcement */}
            <Section title="Announcement Bar" icon={FiBell}>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.showAnnouncement} onChange={(e) => set('showAnnouncement', e.target.checked)} className="w-4 h-4 accent-primary-500" />
                  <span className="text-sm text-gray-700">Show announcement bar</span>
                </label>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Announcement Text</label>
                  <textarea value={form.announcement} onChange={(e) => set('announcement', e.target.value)} rows={3} className="input-field" placeholder="Free shipping on orders above PKR 5,000!" />
                </div>
              </div>
            </Section>

            {/* Currency */}
            <Section title="Currency" icon={FiDollarSign}>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Currency Code</label>
                  <input value={form.currency} onChange={(e) => set('currency', e.target.value)} className="input-field" placeholder="PKR" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Currency Symbol</label>
                  <input value={form.currencySymbol} onChange={(e) => set('currencySymbol', e.target.value)} className="input-field" placeholder="₨" />
                </div>
              </div>
            </Section>

            {/* Shipping */}
            <Section title="Shipping Rules" icon={FiTruck}>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Free Shipping Threshold (PKR)</label>
                  <input type="number" value={form.shipping.freeShippingThreshold} onChange={(e) => setNested('shipping', 'freeShippingThreshold', Number(e.target.value))} className="input-field" min="0" />
                  <p className="text-xs text-gray-400 mt-1">Orders above this amount get free shipping</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Default Shipping Cost (PKR)</label>
                  <input type="number" value={form.shipping.defaultShippingCost} onChange={(e) => setNested('shipping', 'defaultShippingCost', Number(e.target.value))} className="input-field" min="0" />
                </div>
              </div>
            </Section>

            <button type="submit" disabled={saving} className="btn-primary w-full py-3.5 flex items-center justify-center gap-2 text-base">
              {saving ? (
                <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <><FiSave size={18} /> Save All Settings</>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
